import { useState } from 'react';
import type { FeedReply, Post } from '../data/mock';
import { channels } from '../data/mock';
import { usePersonLookup } from '../data/people';
import { Icon } from '../components/Icon';
import { Illustration } from '../components/Illustration';
import {
  Avatar,
  Button,
  Fab,
  IconButton,
  NavBar,
  OrgLogo,
  PersonAvatar,
  Segmented,
  Toggle,
  cx,
} from '../components/ui';
import {
  addPost,
  addReply,
  openDetail,
  pop,
  push,
  setState,
  updatePost,
  useStore,
} from '../state/store';
import { useActiveTheme } from '../state/theme';
import { useWide } from '../shell/adaptive';

const compact = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1).replace('.0', '')}K` : String(n);

function richText(text: string) {
  return text.split(/(\s+)/).map((part, i) =>
    /^#\w+/.test(part) ? (
      <span key={i} className="hashtag">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function FeedScreen() {
  const posts = useStore((s) => s.posts);
  const tab = useStore((s) => s.feedTab);
  const selected = useStore((s) => {
    const top = s.stacks.feed[s.stacks.feed.length - 1];
    return top?.name === 'post' ? top.id : undefined;
  });
  const theme = useActiveTheme();
  const wide = useWide();
  const visible = posts.filter((p) => tab === 'forYou' || p.following);
  return (
    <div className="screen">
      <NavBar
        large
        title={
          <span className="title-with-logo">
            <OrgLogo size={28} />
            Feed
          </span>
        }
        trailing={
          <>
            <IconButton icon="search" label="Search posts" />
            {wide && (
              <IconButton
                icon="compose"
                label="New post"
                onClick={() => setState({ composeOpen: true })}
              />
            )}
          </>
        }
      />
      <div className="screen-scroll">
        {theme.feedHeader && (
          <div className="feed-header">
            <img src={theme.feedHeader} alt="" />
            <div className="feed-header-name">
              <OrgLogo size={36} />
              <span>
                <strong>{theme.orgName}</strong>
                <small>Only people in your organisation see these posts</small>
              </span>
            </div>
          </div>
        )}
        <div className="feed-tabs">
          <Segmented
            value={tab}
            onChange={(v) => setState({ feedTab: v })}
            options={[
              { value: 'forYou', label: 'For you' },
              { value: 'following', label: 'Following' },
            ]}
          />
        </div>
        <div className="post-list">
          {visible.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              selected={selected === p.id}
              onOpen={() =>
                wide
                  ? openDetail({ name: 'post', id: p.id }, 'feed')
                  : push({ name: 'post', id: p.id }, 'feed')
              }
            />
          ))}
        </div>
      </div>
      {!wide && (
        <Fab icon="compose" label="New post" onClick={() => setState({ composeOpen: true })} />
      )}
    </div>
  );
}

function Author({ post }: { post: Post }) {
  const lookup = usePersonLookup();
  const author = lookup(post.author);
  const channel = post.channel ? channels[post.channel] : undefined;
  return (
    <div className="post-author">
      <span className="post-author-name">
        {channel ? channel.name : author.name}
        {channel?.official && (
          <Icon
            name="official"
            size={16}
            filled
            className="official-badge"
            label="Official channel"
          />
        )}
      </span>
      <span className="post-author-meta">
        {channel ? author.name : author.title} · {post.time}
      </span>
    </div>
  );
}

function PostAvatar({ post, size = 44 }: { post: Post; size?: number }) {
  const channel = post.channel ? channels[post.channel] : undefined;
  if (channel) return <Avatar name={channel.name} hue={channel.avatar} size={size} />;
  return <PersonAvatar id={post.author} size={size} />;
}

export function PostCard({
  post,
  onOpen,
  selected,
  detail,
}: {
  post: Post;
  onOpen?: () => void;
  selected?: boolean;
  detail?: boolean;
}) {
  return (
    <article
      className={cx('post', selected && 'is-selected', detail && 'is-detail')}
      onClick={onOpen}
    >
      {post.pinned && (
        <div className="post-context">
          <Icon name="pin" size={13} /> Pinned by admins
        </div>
      )}
      <div className="post-grid">
        <PostAvatar post={post} />
        <div className="post-body">
          <Author post={post} />
          <p className="post-text">{richText(post.text)}</p>
          {post.images && (
            <div className={cx('post-media', `count-${post.images.length}`)}>
              {post.images.map((img, i) => (
                <Illustration key={i} kind={img} />
              ))}
            </div>
          )}
          {post.link && (
            <div className="link-card">
              {post.link.image && <Illustration kind={post.link.image} />}
              <div>
                <span className="link-domain">{post.link.domain}</span>
                <span className="link-title">{post.link.title}</span>
              </div>
            </div>
          )}
          {post.poll && <PollView post={post} />}
          {post.expires && (
            <div className="post-expiry">
              <Icon name="expiry" size={13} /> {post.expires}
            </div>
          )}
          <PostActions post={post} />
        </div>
      </div>
    </article>
  );
}

function PostActions({ post }: { post: Post }) {
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <div className="post-actions">
      <button type="button" className="post-action" aria-label="Reply">
        <Icon name="comment" size={18} /> {compact(post.replies)}
      </button>
      <button
        type="button"
        className={cx('post-action', post.reposted && 'is-reposted')}
        aria-label="Repost"
        onClick={stop(() =>
          updatePost(post.id, (p) => ({
            ...p,
            reposted: !p.reposted,
            reposts: p.reposts + (p.reposted ? -1 : 1),
          })),
        )}
      >
        <Icon name="repost" size={18} /> {compact(post.reposts)}
      </button>
      <button
        type="button"
        className={cx('post-action', post.liked && 'is-liked')}
        aria-label="Like"
        aria-pressed={post.liked}
        onClick={stop(() =>
          updatePost(post.id, (p) => ({
            ...p,
            liked: !p.liked,
            likes: p.likes + (p.liked ? -1 : 1),
          })),
        )}
      >
        <Icon name="like" size={18} filled={post.liked} /> {compact(post.likes)}
      </button>
      <span className="post-action is-static" aria-label="Views">
        <Icon name="views" size={18} /> {compact(post.views)}
      </span>
      <button
        type="button"
        className={cx('post-action', post.bookmarked && 'is-bookmarked')}
        aria-label="Bookmark"
        onClick={stop(() => updatePost(post.id, (p) => ({ ...p, bookmarked: !p.bookmarked })))}
      >
        <Icon name="bookmark" size={18} filled={post.bookmarked} />
      </button>
    </div>
  );
}

function PollView({ post }: { post: Post }) {
  const poll = post.poll!;
  const total = poll.options.reduce((n, o) => n + o.votes, 0);
  const voted = poll.voted;
  return (
    <div className="poll" onClick={(e) => e.stopPropagation()}>
      {poll.options.map((o) => {
        const pct = Math.round((o.votes / total) * 100);
        return voted ? (
          <div key={o.id} className={cx('poll-result', voted === o.id && 'is-mine')}>
            <span className="poll-fill" style={{ width: `${pct}%` }} />
            <span className="poll-label">
              {o.label} {voted === o.id && <Icon name="tickSent" size={15} strokeWidth={2.4} />}
            </span>
            <span className="poll-pct">{pct}%</span>
          </div>
        ) : (
          <button
            key={o.id}
            type="button"
            className="poll-option"
            onClick={() =>
              updatePost(post.id, (p) => ({
                ...p,
                poll: {
                  ...poll,
                  voted: o.id,
                  options: poll.options.map((x) =>
                    x.id === o.id ? { ...x, votes: x.votes + 1 } : x,
                  ),
                },
              }))
            }
          >
            {o.label}
          </button>
        );
      })}
      <div className="poll-meta">
        {total + (voted ? 0 : 0)} votes · {poll.endsIn}
        {poll.anonymous && ' · Anonymous'}
      </div>
    </div>
  );
}

export function PostDetail({ id }: { id: string }) {
  const post = useStore((s) => s.posts.find((p) => p.id === id));
  const replies = useStore((s) => s.replies);
  const wide = useWide();
  const [draft, setDraft] = useState('');
  if (!post) return null;
  const thread = replies.filter((r) => r.postId === id);
  return (
    <div className="screen">
      <NavBar
        title="Post"
        onBack={wide ? undefined : pop}
        center={!wide}
        trailing={<IconButton icon="share" label="Share" />}
      />
      <div className="screen-scroll">
        <PostCard post={post} detail />
        <div className="replies">
          {thread.map((r) => (
            <ReplyView key={r.id} reply={r} />
          ))}
        </div>
      </div>
      <div className="composer">
        <PersonAvatar id="me" size={32} />
        <label className="composer-field">
          <textarea
            rows={1}
            placeholder="Post your reply"
            aria-label="Reply"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
                e.preventDefault();
                addReply(id, draft.trim());
                setDraft('');
              }
            }}
          />
        </label>
        <Button
          small
          disabled={!draft.trim()}
          onClick={() => {
            addReply(id, draft.trim());
            setDraft('');
          }}
        >
          Reply
        </Button>
      </div>
    </div>
  );
}

function ReplyView({ reply, nested }: { reply: FeedReply; nested?: boolean }) {
  const lookup = usePersonLookup();
  const p = lookup(reply.author);
  const [liked, setLiked] = useState(false);
  return (
    <div className={cx('reply', nested && 'is-nested')}>
      <PersonAvatar id={reply.author} size={nested ? 30 : 36} />
      <div className="reply-body">
        <div className="post-author">
          <span className="post-author-name">{reply.author === 'me' ? 'You' : p.name}</span>
          <span className="post-author-meta">· {reply.time}</span>
        </div>
        <p className="post-text">{reply.text}</p>
        <div className="post-actions small">
          <button type="button" className="post-action">
            <Icon name="comment" size={16} /> Reply
          </button>
          <button
            type="button"
            className={cx('post-action', liked && 'is-liked')}
            onClick={() => setLiked(!liked)}
          >
            <Icon name="like" size={16} filled={liked} /> {reply.likes + (liked ? 1 : 0)}
          </button>
        </div>
        {reply.replies?.map((r) => (
          <ReplyView key={r.id} reply={r} nested />
        ))}
      </div>
    </div>
  );
}

const MAX = 1000;

export function ComposePost() {
  const wide = useWide();
  const theme = useActiveTheme();
  const [text, setText] = useState('');
  const [images, setImages] = useState<Post['images']>([]);
  const [poll, setPoll] = useState<string[] | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [expires, setExpires] = useState(false);
  const [as, setAs] = useState<'me' | 'hr'>('me');
  const close = () => setState({ composeOpen: false });
  const left = MAX - text.length;
  const canPost =
    text.trim().length > 0 && left >= 0 && (!poll || poll.filter((o) => o.trim()).length >= 2);

  return (
    <div className={cx('compose-layer', wide && 'is-dialog')} onClick={wide ? close : undefined}>
      <div
        className="compose"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="New post"
      >
        <header className="compose-head">
          <button type="button" className="text-button" onClick={close}>
            Cancel
          </button>
          <span className="compose-title">New post</span>
          <Button
            small
            disabled={!canPost}
            onClick={() =>
              addPost({
                author: 'me',
                channel: as === 'hr' ? 'hr' : undefined,
                time: 'now',
                text,
                images: images?.length ? images : undefined,
                poll: poll
                  ? {
                      options: poll
                        .filter((o) => o.trim())
                        .map((label, i) => ({ id: String(i), label, votes: 0 })),
                      endsIn: '7 days left',
                      anonymous,
                    }
                  : undefined,
                expires: expires ? 'Expires in 48 h' : undefined,
                likes: 0,
                replies: 0,
                reposts: 0,
                views: 1,
                following: true,
              })
            }
          >
            Post
          </Button>
        </header>
        <div className="compose-body">
          <div className="compose-as">
            <PersonAvatar id="me" size={40} />
            <Segmented
              value={as}
              onChange={setAs}
              options={[
                { value: 'me', label: 'As Anna Berg' },
                { value: 'hr', label: 'As HR News' },
              ]}
            />
          </div>
          <textarea
            className="compose-text"
            placeholder={`What’s new at ${theme.orgName ?? 'your organisation'}?`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            aria-label="Post text"
          />
          {images && images.length > 0 && (
            <div className={cx('post-media', `count-${images.length}`)}>
              {images.map((img, i) => (
                <div key={i} className="compose-media">
                  <Illustration kind={img} />
                  <button
                    type="button"
                    className="media-remove"
                    aria-label="Remove image"
                    onClick={() => setImages(images.filter((_, j) => j !== i))}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {poll && (
            <div className="poll-builder">
              {poll.map((o, i) => (
                <input
                  key={i}
                  className="input"
                  value={o}
                  placeholder={`Option ${i + 1}${i > 1 ? ' (optional)' : ''}`}
                  onChange={(e) => setPoll(poll.map((x, j) => (j === i ? e.target.value : x)))}
                />
              ))}
              {poll.length < 4 && (
                <button type="button" className="text-link" onClick={() => setPoll([...poll, ''])}>
                  + Add option
                </button>
              )}
              <div className="inline-setting">
                <span>Poll length</span>
                <strong>7 days</strong>
              </div>
              <div className="inline-setting">
                <span>Anonymous votes</span>
                <Toggle checked={anonymous} onChange={setAnonymous} label="Anonymous votes" />
              </div>
              <button type="button" className="text-link danger" onClick={() => setPoll(null)}>
                Remove poll
              </button>
            </div>
          )}
          <div className="inline-setting">
            <span>
              <Icon name="expiry" size={16} /> Expires after 48 hours
            </span>
            <Toggle checked={expires} onChange={setExpires} label="Expires after 48 hours" />
          </div>
        </div>
        <footer className="compose-tools">
          <IconButton
            icon="photo"
            label="Add images (up to 4)"
            onClick={() =>
              images &&
              images.length < 4 &&
              !poll &&
              setImages([
                ...images,
                (['trucks', 'harbour', 'team', 'pallets'] as const)[images.length]!,
              ])
            }
          />
          <IconButton
            icon="poll"
            label="Add poll"
            onClick={() => !images?.length && setPoll(poll ?? ['', ''])}
          />
          <IconButton
            icon="hashtag"
            label="Hashtag"
            onClick={() => setText(`${text}${text && !text.endsWith(' ') ? ' ' : ''}#`)}
          />
          <span className="compose-audience">
            <Icon name="lock" size={13} /> {theme.orgName ?? 'Organisation'} only
          </span>
          <span className={cx('char-count', left < 100 && 'is-low', left < 0 && 'is-over')}>
            {left}
          </span>
        </footer>
      </div>
    </div>
  );
}
